import {
  faCheck,
  faCheckCircle,
  faChevronDown,
  faChevronRight,
  faCopy,
  faPen,
  faRotateRight,
  faSpinner,
  faTrash,
} from "@fortawesome/pro-regular-svg-icons";
import { faPlay } from "@fortawesome/pro-solid-svg-icons";
import { useIsMutating } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { AnimatePresence, LayoutGroup, m } from "framer-motion";
import React, { useCallback, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import { AgentVersionStatusIndicator } from "src/agents/AgentVersionName";
import { AgentPlaygroundDrawer } from "src/agents/AgentVersionPage/AgentPlaygroundDrawer";
import { AddToolDrawer } from "src/agents/AgentVersionPage/AgentToolsEditor/AddToolDrawer";
import { FlowToolConfigDrawer } from "src/agents/AgentVersionPage/AgentToolsEditor/FlowToolConfigDrawer";
import {
  AgentVersionConfigPanel,
  AgentVersionConfigPanelSkeleton,
} from "src/agents/AgentVersionPage/AgentVersionConfigPanel";
import {
  JsonSchemaDrawer,
  JsonSchemaEditorConfig,
} from "src/agents/AgentVersionPage/JsonSchemaDrawer";
import { PlaygroundProvider } from "src/agents/AgentVersionPage/PlaygroundContext";
import { CreateAgentVersionModal } from "src/agents/CreateAgentVersionModal";
import { EditAgentVersionModal } from "src/agents/EditAgentVersionModal";
import {
  DrawerContainer,
  SideDrawerCoordinatorProvider,
  useSideDrawerCoordinator,
} from "src/agents/base/SideDrawer";
import {
  AGENT_VERSION_CONFIGURATION_MUTATION_KEY,
  useAgent,
  useAgentVersion,
  useAgentVersions,
  useDeleteAgentVersion,
  usePublishAgentVersion,
} from "src/agents/queries/management";
import { AgentVersion } from "src/agents/types";
import { Button } from "src/base-components/Button";
import { ConfirmationModal } from "src/base-components/ConfirmationModal";
import { DisplayDateFromNow } from "src/base-components/Date";
import { FixedPositionedDropdown } from "src/base-components/FixedPositionedDropDown";
import { Icon } from "src/base-components/Icon";
import { EllipsisOptionsDropdown } from "src/base-components/OptionsDropdown/EllipsisOptionsDropdown";
import { OptionsDropdownElement } from "src/base-components/OptionsDropdown/OptionsDropdownItems";
import { Pill } from "src/base-components/Pill";
import { SkeletonPlaceholder } from "src/base-components/SkeletonPlaceholder";
import { EmptyState } from "src/design-system/EmptyState";
import { useModal } from "src/design-system/Modal";
import { toastActions } from "src/design-system/Toast/utils";
import { useDocumentTitle } from "src/hooks/useDocumentTitle";
import { WhitePane } from "src/jobs/common/WhitePane";
import { DashboardContent } from "src/layout/DashboardContent";
import { PageHeader } from "src/layout/PageHeader";
import {
  AgentVersionPageParams,
  DashboardPageParams,
  getUrlToAgentsPage,
  getUrlToAgentVersionPage,
  getUrlToAgentVersionsPage,
} from "src/router/urls";
import { useParamsDecode } from "src/utils/useParamsDecode";

// Re-export for external consumers
export type { AgentSchemaPropertyUI } from "src/agents/AgentVersionPage/AgentSchemaEditor";
export { convertAgentSchemaBEtoUI } from "src/agents/AgentVersionPage/AgentSchemaEditor";

export const AgentVersionPage: React.FC = () => {
  return (
    <SideDrawerCoordinatorProvider>
      <AgentVersionPageContent />
    </SideDrawerCoordinatorProvider>
  );
};

const AgentVersionPageContent: React.FC = () => {
  useDocumentTitle(["Edit Agent Version", "Agents"]);

  const { orgId, wsId, agentId, versionId } =
    useParamsDecode<AgentVersionPageParams>();
  const navigate = useNavigate();

  const agentQuery = useAgent(agentId);
  const versionsQuery = useAgentVersions(agentId);
  const versionQuery = useAgentVersion(agentId, versionId);
  const publishMutation = usePublishAgentVersion();

  const {
    isOpen: isDuplicateModalOpen,
    openModal: openDuplicateModal,
    closeModal: closeDuplicateModal,
  } = useModal();

  const {
    isOpen: isEditModalOpen,
    openModal: openEditModal,
    closeModal: closeEditModal,
  } = useModal();

  const {
    isOpen: isDeleteModalOpen,
    openModal: openDeleteModal,
    closeModal: closeDeleteModal,
  } = useModal();

  const deleteMutation = useDeleteAgentVersion();

  const currentVersion = versionQuery.data;

  const [jsonEditorConfig, setJsonEditorConfig] =
    useState<JsonSchemaEditorConfig | null>(null);

  // Get drawer state from coordinator
  const { openDrawer, exitingDrawerId } = useSideDrawerCoordinator();
  const isAnyDrawerOpen = openDrawer !== null || exitingDrawerId !== null;
  const isPlaygroundPanelOpen = openDrawer?.type === "playground";

  const handlePublish = async () => {
    if (!currentVersion) return;

    try {
      await publishMutation.mutateAsync({
        agentId,
        versionId,
        etag: currentVersion.etag,
      });
      toastActions.success({
        title: "Agent version published successfully",
      });
    } catch (error) {
      toastActions.failure({
        title: "Failed to publish agent version",
        description:
          error instanceof Error ? error.message : "An unknown error occurred",
      });
    }
  };

  const handleConfirmDelete = async () => {
    try {
      await deleteMutation.mutateAsync({ agentId, versionId });
      closeDeleteModal();

      // Navigate to another version if available, otherwise to the agent versions page
      const otherVersions = versionsQuery.data?.filter(
        (v) => v.id !== versionId,
      );
      if (otherVersions && otherVersions.length > 0) {
        navigate(
          getUrlToAgentVersionPage(orgId, wsId, agentId, otherVersions[0].id),
        );
      } else {
        navigate(getUrlToAgentVersionsPage(orgId, wsId, agentId));
      }

      toastActions.success({
        title: "Version deleted successfully",
      });
    } catch (error) {
      toastActions.failure({
        title: "Failed to delete version",
        description:
          error instanceof Error ? error.message : "An unknown error occurred",
      });
    }
  };

  const getPageHeaderTitle = () => {
    if (agentQuery.isLoading || versionsQuery.isLoading) {
      return (
        <div className="flex items-center gap-x-2">
          <SkeletonPlaceholder height="h-5" width="w-36" />
          <Icon icon={faChevronRight} size="xs" />
          <SkeletonPlaceholder height="h-5" width="w-36" />
        </div>
      );
    }

    if (agentQuery.data && versionsQuery.data) {
      return (
        <div className="flex items-center gap-x-2">
          <Link
            className="flex items-center gap-x-2"
            to={getUrlToAgentVersionsPage(orgId, wsId, agentId)}
          >
            <div className="max-w-96 truncate">{agentQuery.data.name}</div>{" "}
          </Link>
          <Icon icon={faChevronRight} size="xs" />
          <VersionPicker versions={versionsQuery.data} />
        </div>
      );
    }

    const isNotFound =
      (agentQuery.error as AxiosError)?.response?.status === 404 ||
      (versionsQuery.error as AxiosError)?.response?.status === 404;

    return isNotFound ? "Agent not found" : "Error loading agent";
  };

  const MAX_FORM_WIDTH = 950;
  return (
    <DashboardContent
      Header={
        <PageHeader
          backLink={getUrlToAgentVersionsPage(orgId, wsId, agentId)}
          sideContent={
            <div className="flex items-center gap-x-3">
              {agentQuery.isLoading || !currentVersion ? (
                <>
                  <SkeletonPlaceholder height="h-5" width="w-36" />
                  <SkeletonPlaceholder height="h-5" width="w-16" />
                  <SkeletonPlaceholder height="h-5" width="w-5" />
                </>
              ) : (
                <LayoutGroup>
                  <m.div layout="position">
                    <UpdatedInfo version={currentVersion} />
                  </m.div>
                  <AnimatePresence initial={false}>
                    {!isPlaygroundPanelOpen && (
                      <m.div
                        animate={{
                          opacity: 1,
                          transition: {
                            duration: 0.2,
                            ease: "easeOut",
                            delay: 0.4,
                          },
                        }}
                        exit={{
                          opacity: 0,
                          transition: {
                            duration: 0.1,
                            ease: "easeIn",
                          },
                        }}
                        initial={{ opacity: 0 }}
                      >
                        <TestAgentButton />
                      </m.div>
                    )}
                  </AnimatePresence>
                  <PageMenu
                    isDraft={currentVersion?.status === "draft"}
                    isPublishing={publishMutation.isPending}
                    onDelete={openDeleteModal}
                    onDuplicate={openDuplicateModal}
                    onEdit={openEditModal}
                    onPublish={handlePublish}
                  />
                </LayoutGroup>
              )}
            </div>
          }
          title={getPageHeaderTitle()}
        />
      }
      removePadding
    >
      <div
        className="flex h-full flex-col bg-gray-100"
        data-loc="agent-version-page"
      >
        {versionQuery.error ? (
          <AgentVersionErrorState
            error={versionQuery.error}
            refetch={versionQuery.refetch}
          />
        ) : (
          <PlaygroundProvider agentId={agentId} versionId={versionId}>
            <div className="flex h-full">
              {/* Left: Scrollable Configuration Form Area */}
              <div
                className={`flex min-w-[500px] flex-1 overflow-auto bg-gray-100 py-5 pl-5 pr-5 ${
                  isAnyDrawerOpen
                    ? "nativeScrollbarStyled"
                    : "decideScrollbar compensate-scrollbar-gutter"
                }`}
              >
                <div
                  className="mx-auto w-full"
                  style={{
                    maxWidth: isAnyDrawerOpen ? undefined : MAX_FORM_WIDTH,
                  }}
                >
                  {!currentVersion ? (
                    <AgentVersionConfigPanelSkeleton />
                  ) : (
                    <AgentVersionConfigPanel
                      key={currentVersion.id}
                      version={currentVersion}
                      onOpenJsonEditor={setJsonEditorConfig}
                    />
                  )}
                </div>
              </div>

              {/* Right: Drawers (wrapped for smooth transitions) */}
              <DrawerContainer>
                <JsonSchemaDrawer config={jsonEditorConfig} />
                <AgentPlaygroundDrawer
                  agentId={agentId}
                  agentName={agentQuery.data?.name ?? "Agent"}
                  versionId={versionId}
                />
                <AddToolDrawer />
                <FlowToolConfigDrawer />
              </DrawerContainer>
            </div>
          </PlaygroundProvider>
        )}
      </div>
      <CreateAgentVersionModal
        agentId={agentId}
        open={isDuplicateModalOpen}
        startFromScratch={false}
        versionId={versionId}
        onClose={closeDuplicateModal}
        onCreateSuccess={(createdAgentId, newVersion) =>
          navigate(
            getUrlToAgentVersionPage(
              orgId,
              wsId,
              createdAgentId,
              newVersion.id,
            ),
          )
        }
      />
      <EditAgentVersionModal
        open={isEditModalOpen}
        version={currentVersion ?? null}
        onClose={closeEditModal}
      />
      <ConfirmationModal
        confirmationButtonText="Delete"
        description={`Are you sure you want to delete version "${currentVersion?.name}"? This action cannot be undone.`}
        open={isDeleteModalOpen}
        title="Delete version"
        variant="danger"
        onClose={closeDeleteModal}
        onConfirm={handleConfirmDelete}
      />
    </DashboardContent>
  );
};

const AgentVersionErrorState: React.FC<{
  error: Error;
  refetch: () => void;
}> = ({ refetch, error }) => {
  const { orgId, wsId } = useParamsDecode<DashboardPageParams>();
  const navigate = useNavigate();
  const isNotFoundError =
    error instanceof AxiosError && error?.response?.status === 404;

  if (isNotFoundError) {
    return (
      <div className="m-auto flex w-[1084px] flex-1 gap-x-5 bg-gray-50 py-10">
        <WhitePane>
          <EmptyState
            actionButton={{
              text: "Go to Agents",
              onClick: () => navigate(getUrlToAgentsPage({ orgId, wsId })),
            }}
            description="The agent you are looking for does not exist."
            headline="Agent not found"
            icon={faRotateRight}
            variant="error"
          />
        </WhitePane>
      </div>
    );
  }

  return (
    <div className="m-auto flex w-[1084px] flex-1 gap-x-5 bg-gray-50 py-10">
      <WhitePane>
        <EmptyState
          actionButton={{
            icon: faRotateRight,
            text: "Retry",
            onClick: refetch,
          }}
          description="We're having trouble loading Agent Version. Please try again later or contact Taktile support if the problem persists."
          headline="Error loading Agent Version"
          icon={faRotateRight}
          variant="error"
        />
      </WhitePane>
    </div>
  );
};

type VersionPickerProps = {
  versions: AgentVersion[];
};

const VersionPicker: React.FC<VersionPickerProps> = ({ versions }) => {
  const navigate = useNavigate();
  const { orgId, wsId, agentId, versionId } =
    useParamsDecode<AgentVersionPageParams>();

  return (
    <FixedPositionedDropdown
      buttonClassName="rounded-full border-none max-w-64"
      dataLoc="current-version-picker"
      elements={versions.map((version) => ({
        key: version.id,
        value: version,
      }))}
      placement="bottom start"
      renderButtonValue={(version) =>
        version ? (
          <Pill
            dataLoc="current-version-picker-button"
            size="lg"
            variant="outlined-white"
            fullWidth
          >
            <AgentVersionStatusIndicator status={version.status} />
            <Pill.Text>{version.name}</Pill.Text>
            <Pill.Icon icon={faChevronDown} size="base" />
          </Pill>
        ) : (
          <SkeletonPlaceholder height="h-5" width="w-36" />
        )
      }
      renderValue={({ value: version, selected }) => (
        <div className="flex items-center justify-between gap-x-2">
          <div className="flex items-center gap-x-1.5">
            <AgentVersionStatusIndicator status={version.status} />
            <span>{version.name}</span>
          </div>
          {selected && (
            <Icon color="text-indigo-500" icon={faCheck} size="sm" />
          )}
        </div>
      )}
      selected={versionId}
      onSelect={(versionId) =>
        navigate(getUrlToAgentVersionPage(orgId, wsId, agentId, versionId))
      }
    />
  );
};

const UpdatedInfo: React.FC<{
  version: AgentVersion;
}> = ({ version }) => {
  const isSaving = !!useIsMutating({
    mutationKey: AGENT_VERSION_CONFIGURATION_MUTATION_KEY,
  });

  const isPublished = version.status === "published";
  const dateValue = isPublished
    ? version.metadata.published_at
    : version.metadata.updated_at;
  const prefix = isPublished ? "Published" : "Updated";

  if (!dateValue) {
    return null;
  }

  const variant = isPublished ? "green" : "indigo-light";
  const overlayBgClass = isPublished ? "bg-green-50" : "bg-indigo-50";
  const overlayTextClass = isPublished ? "text-green-700" : "text-indigo-500";
  const overlayIconClass = isPublished ? "text-green-400" : "text-indigo-400";

  return (
    <Pill dataLoc="last-updated-info" variant={variant}>
      <div className="relative flex items-center">
        <Pill.Icon icon={faCheck} />
        <Pill.Text fontType="text">
          {prefix} <DisplayDateFromNow value={dateValue} />
        </Pill.Text>
        <AnimatePresence>
          {isSaving && (
            <m.div
              animate={{ opacity: 1 }}
              className={`absolute inset-0 flex items-center ${overlayBgClass}`}
              exit={{ opacity: 0 }}
              initial={{ opacity: 0 }}
              transition={{ duration: 0.15 }}
            >
              <Icon color={overlayIconClass} icon={faSpinner} size="2xs" spin />
              <span
                className={`mx-1 font-inter-medium-12px ${overlayTextClass}`}
              >
                Saving...
              </span>
            </m.div>
          )}
        </AnimatePresence>
      </div>
    </Pill>
  );
};

const PageMenu: React.FC<{
  isDraft?: boolean;
  isPublishing?: boolean;
  onDelete: () => void;
  onDuplicate: () => void;
  onEdit: () => void;
  onPublish: () => void;
}> = ({ isDraft, isPublishing, onDelete, onDuplicate, onEdit, onPublish }) => {
  const options = [
    isDraft && {
      key: "Edit version details",
      icon: faPen,
      action: onEdit,
    },
    {
      key: "Duplicate",
      icon: faCopy,
      action: onDuplicate,
    },
    isDraft && {
      key: "Publish Agent",
      icon: faCheckCircle,
      action: onPublish,
      disabled: isPublishing,
    },
    isDraft && {
      key: "Delete version",
      icon: faTrash,
      action: onDelete,
      destructive: true,
    },
  ].filter(Boolean) as OptionsDropdownElement[];

  return (
    <EllipsisOptionsDropdown dataLoc="version-page-menu" elements={options} />
  );
};

/**
 * Test agent button that opens the playground drawer via coordinator.
 * Exported for use in component tests.
 */
export const TestAgentButton: React.FC = () => {
  const { requestOpen } = useSideDrawerCoordinator();

  const handleClick = useCallback(() => {
    requestOpen({ type: "playground" });
  }, [requestOpen]);

  return (
    <Button
      dataLoc="test-agent-button"
      iconLeft={faPlay}
      size="sm"
      variant="primary"
      onClick={handleClick}
    >
      Test
    </Button>
  );
};
